/**
 * @fileoverview Classe de utilidades para desenvolvimento
 */

import { FormGroup } from '@angular/forms';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

/**
 * @class Masks
 * @description Classe de máscaras para inputs
 * @example
 * import { Masks } from 'path/to/Utils';
 *
 * Masks.cpf('12345678901');
 * // 123.456.789-01
 *
 * Masks.cpf('123.456.789-01');
 * // 123.456.789-01
 *
 * Masks.rg('123456789');
 * // 12.345.678-9
 */
export class Masks {
  cpf(cpf: string): string {
    cpf = cpf.replace(/[^\d]+/g, '');
    if (cpf.length > 3) {
      cpf = cpf.substring(0, 3) + '.' + cpf.substring(3);
    }
    if (cpf.length > 7) {
      cpf = cpf.substring(0, 7) + '.' + cpf.substring(7);
    }
    if (cpf.length > 11) {
      cpf = cpf.substring(0, 11) + '-' + cpf.substring(11, 13);
    }
    return cpf;
  }

  rg(rg: string): string {
    rg = rg.replace(/[^\d]+/g, '');
    if (rg.length > 2) {
      rg = rg.substring(0, 2) + '.' + rg.substring(2);
    }
    if (rg.length > 6) {
      rg = rg.substring(0, 6) + '.' + rg.substring(6);
    }
    if (rg.length > 10) {
      rg = rg.substring(0, 10) + '-' + rg.substring(10, 11);
    }
    return rg;
  }

  cnpj(cnpj: string): string {
    cnpj = cnpj.replace(/[^\d]+/g, '');
    if (cnpj.length > 2) {
      cnpj = cnpj.substring(0, 2) + '.' + cnpj.substring(2);
    }
    if (cnpj.length > 6) {
      cnpj = cnpj.substring(0, 6) + '.' + cnpj.substring(6);
    }
    if (cnpj.length > 10) {
      cnpj = cnpj.substring(0, 10) + '/' + cnpj.substring(10);
    }
    if (cnpj.length > 15) {
      cnpj = cnpj.substring(0, 15) + '-' + cnpj.substring(15, 17);
    }
    return cnpj;
  }

  cep(cep: string): string {
    cep = cep.replace(/[^\d]+/g, '');
    if (cep.length > 5) {
      cep = cep.substring(0, 5) + '-' + cep.substring(5, 8);
    }
    return cep;
  }

  telefone(telefone: string): string {
    telefone = telefone.replace(/[^\d]+/g, '');
    if (telefone.length > 0) {
      telefone = '(' + telefone;
    }
    if (telefone.length > 3) {
      telefone = telefone.substring(0, 3) + ') ' + telefone.substring(3);
    }
    if (telefone.length > 10) {
      telefone = telefone.substring(0, 10) + '-' + telefone.substring(10, 14);
    }
    return telefone;
  }

  data(data: string): string {
    data = data.replace(/[^\d]+/g, '');
    if (data.length > 2) {
      data = data.substring(0, 2) + '/' + data.substring(2);
    }
    if (data.length > 5) {
      data = data.substring(0, 5) + '/' + data.substring(5, 9);
    }
    return data;
  }
}

/**
 * @class Validators
 * @description Classe de validações para inputs
 * @example
 * import { Validators } from 'path/to/Utils';
 *
 * Validators.isEmail('teste');
 * // false
 *
 * Validators.isEmail('teste@mail.c');
 * // false
 *
 * Validators.isEmail('teste@mail.com');
 * // true
 */
export class Validators extends Masks {
  /**
   * @description yyyy-MM-dd
   */
  public static DATE_YYYY_MM_DD_REGEX = /^\d{4}-\d{2}-\d{2}$/;

  public static isEmail(email: string): boolean {
    const regex =
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return regex.test(String(email).toLowerCase());
  }

  public static isCPF(cpf: string): boolean {
    cpf = cpf.replace(/[^\d]+/g, '');
    if (cpf == '') return false;
    // Elimina CPFs invalidos conhecidos
    if (
      cpf.length != 11 ||
      cpf == '00000000000' ||
      cpf == '11111111111' ||
      cpf == '22222222222' ||
      cpf == '33333333333' ||
      cpf == '44444444444' ||
      cpf == '55555555555' ||
      cpf == '66666666666' ||
      cpf == '77777777777' ||
      cpf == '88888888888' ||
      cpf == '99999999999'
    )
      return false;
    // Valida 1o digito
    let add = 0;
    for (let i = 0; i < 9; i++) add += parseInt(cpf.charAt(i)) * (10 - i);
    let rev = 11 - (add % 11);
    if (rev == 10 || rev == 11) rev = 0;
    if (rev != parseInt(cpf.charAt(9))) return false;
    // Valida 2o digito
    add = 0;
    for (let i = 0; i < 10; i++) add += parseInt(cpf.charAt(i)) * (11 - i);
    rev = 11 - (add % 11);
    if (rev == 10 || rev == 11) rev = 0;
    if (rev != parseInt(cpf.charAt(10))) return false;
    return true;
  }

  public static isCNPJ(cnpj: string): boolean {
    cnpj = cnpj.replace(/[^\d]+/g, '');

    if (cnpj == '') return false;

    if (cnpj.length != 14) return false;

    // Elimina CNPJs invalidos conhecidos
    if (
      cnpj == '00000000000000' ||
      cnpj == '11111111111111' ||
      cnpj == '22222222222222' ||
      cnpj == '33333333333333' ||
      cnpj == '44444444444444' ||
      cnpj == '55555555555555' ||
      cnpj == '66666666666666' ||
      cnpj == '77777777777777' ||
      cnpj == '88888888888888' ||
      cnpj == '99999999999999'
    )
      return false;

    // Valida DVs
    let tamanho = cnpj.length - 2;
    let numeros = cnpj.substring(0, tamanho);
    let digitos = cnpj.substring(tamanho);
    let soma = 0;
    let pos = tamanho - 7;
    for (let i = tamanho; i >= 1; i--) {
      soma += parseInt(numeros.charAt(tamanho - i)) * pos--;
      if (pos < 2) pos = 9;
    }
    let resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
    if (resultado != parseInt(digitos.charAt(0))) return false;

    tamanho = tamanho + 1;
    numeros = cnpj.substring(0, tamanho);
    soma = 0;
    pos = tamanho - 7;
    for (let i = tamanho; i >= 1; i--) {
      soma += parseInt(numeros.charAt(tamanho - i)) * pos--;
      if (pos < 2) pos = 9;
    }
    resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
    if (resultado != parseInt(digitos.charAt(1))) return false;

    return true;
  }

  /**
   *
   * @param date "dd/mm/yyyy"
   * @returns boolean
   *
   * @example
   * var resultado = Validators.isDate('31/02/2019');
   * // true
   *
   * var resultado = Validators.isDate('');
   *
   */
  public static isDate(date: string): boolean {
    if (!date) return false;

    // dd/mm/yyyy -> yyyy-mm-dd
    if (date.charAt(2) === '/') date = date.split('/').reverse().join('-');

    return !isNaN(Date.parse(date));
  }

  /**
   * @param date "dd/mm/yyyy"
   * @returns boolean
   *
   * @example
   * var resultado = Validators.isValidDate('31/02/2019');
   * // false
   *
   * var resultado = Validators.isValidDate('29/02/2019');
   * // true
   */
  public static isValidDate(date: string): boolean {
    const regex = /^\d{2}\/\d{2}\/\d{4}$/;

    if (!regex.test(date)) return false;

    const parts = date.split('/');

    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);

    if (year < 1000 || year > 3000 || month == 0 || month > 12) return false;

    const daysInMonth = new Date(year, month, 0).getDate();

    return day > 0 && day <= daysInMonth;
  }

  /**
   *
   * @param rg "00.000.000-0"
   * @returns boolean
   *
   * @example
   * var resultado = Validators.isRG('00.000.000-0');
   * // false
   *
   * var resultado = Validators.isRG('');
   * // false
   *
   * var resultado = Validators.isRG('18.233.670-0);
   * // true
   */
  public static isRG(rg: string): boolean {
    if (!rg) return false;

    rg = rg.replace(/[\s.-]*/gim, '');

    if (!rg || rg.length <= 0) {
      return false;
    } else if (
      rg.length != 9 ||
      rg == '000000000' ||
      rg == '111111111' ||
      rg == '222222222' ||
      rg == '333333333' ||
      rg == '444444444' ||
      rg == '555555555' ||
      rg == '666666666' ||
      rg == '777777777' ||
      rg == '888888888' ||
      rg == '999999999'
    ) {
      return false;
    }

    return true;
  }

  public static isNumber(value: string): boolean {
    return !isNaN(Number(value));
  }
}

/**
 * @class DevUtils
 * @description Classe de utilidades para desenvolvimento
 * @example
 * import { DevUtils } from 'path/to/Utils';
 *
 * DevUtils.findByKey('id', [{ id: 1, name: 'Teste' }]);
 * // { id: 1, name: 'Teste' }
 *
 * DevUtils.searchInArray([{ id: 1, name: 'Teste' }, { id: 2, name: 'SPTX' }], 'SPTX', ['name']);
 * // { id: 2, name: 'SPTX' }
 *
 */
export class DevUtils extends Validators {
  constructor() {
    super();

    throw new Error('This class cannot be instantiated');
  }

  public static promiseExecution = async (promises: any[]) => {
    // Iterates over an array of promises and awaits each one sequentially
    for (let promise of promises) {
      try {
        // Waits for each promise to resolve
        const message = await promise;
        console.log(message);
        // Logs the resolved value
      } catch (error) {
        // Catches and logs any errors
        console.error(error.message);
      }
    }
  };

  public static parseBPMEncodedString(str: string) {

    if(!str)
      return str;

    return DevUtils.replaceAllFallback(DevUtils.urlDecode(decodeURIComponent(str)), "+", " ");
  }

  public static sleep(ms: 1000)
  {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
  public static capitalize = str => (str && String(str[0]).toUpperCase() + String(str).slice(1).toLowerCase()) || ""

  /**
   * rfc3986 compatable encode of a string
   *
   * @param {String} string
   */
  public static urlEncode = function (string) {
    function hex(code) {
      var hex = code.toString(16).toUpperCase();
      if (hex.length < 2) {
        hex = 0 + hex;
      }
      return '%' + hex;
    }

    if (!string) {
      return '';
    }

    string = string + '';
    var reserved_chars = /[ \r\n!*"'();:@&=+$,\/?%#\[\]<>{}|`^\\\u0080-\uffff]/,
      str_len = string.length,
      i,
      string_arr = string.split(''),
      c;

    for (i = 0; i < str_len; i++) {
      if ((c = string_arr[i].match(reserved_chars))) {
        c = c[0].charCodeAt(0);

        if (c < 128) {
          string_arr[i] = hex(c);
        } else if (c < 2048) {
          string_arr[i] = hex(192 + (c >> 6)) + hex(128 + (c & 63));
        } else if (c < 65536) {
          string_arr[i] =
            hex(224 + (c >> 12)) +
            hex(128 + ((c >> 6) & 63)) +
            hex(128 + (c & 63));
        } else if (c < 2097152) {
          string_arr[i] =
            hex(240 + (c >> 18)) +
            hex(128 + ((c >> 12) & 63)) +
            hex(128 + ((c >> 6) & 63)) +
            hex(128 + (c & 63));
        }
      }
    }
    return string_arr.join('');
  };

  public static replaceAllFallback(str, search, replacement) {

    search = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    const regex = new RegExp(search, 'g');
    return str.replace(regex, replacement);
  }

  /**
   * rfc3986 compatable decode of a string
   *
   * @param {String} string
   */
  public static urlDecode = function (string: string): string {
    if (!string) {
      return '';
    }
    return string.replace(/%[a-fA-F0-9]{2}/gi, function (match) {
      return String.fromCharCode(parseInt(match.replace('%', ''), 16));
    });
  };

  public static generateGuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
      /[xy]/g,
      function (c) {
        var r = (Math.random() * 16) | 0,
          v = c == 'x' ? r : (r & 3) | 8;
        return v.toString(16);
      }
    );
  }

  /**
   *
   * @param itemHeight Altura do item em px
   * @param headHeight Altura do que esta acima da listagem em px
   * @param headHeightCompensation Compensação de altura do que esta acima da listagem em px
   * @param pageHeight Altura da página
   * @returns Quantidade de itens para preencher a página de acordo com a altura do item
   */
  public static calculateItensByPageHeight(
    itemHeight: number,
    headHeight: number,
    headHeightCompensation = 20,
    pageHeight: number = window.innerHeight
  ): number {
    let qtdItens = pageHeight - headHeight + headHeightCompensation;
    qtdItens = qtdItens + itemHeight;
    qtdItens = qtdItens / itemHeight;
    qtdItens = Math.round(qtdItens);
    qtdItens += 5;

    return qtdItens;
  }

  /**
   * @description Busca por um item dentro de um array por sua chave
   * @param key Valor chave do objeto a ser retornado
   * @param array Array de objetos
   * @returns Retorna um objeto dentro de um array de objetos pelo valor chave.
   */
  public static findByKey<T>(key: string, array: T[]): T {
    return array.find((item: T) => {
      return item[key];
    });
  }

  /**
   * @description Filtra um array de acordo com uma string de busca e um array de parâmetros
   * @param array Array a ser filtrado de acordo com o searchStr
   * @param searchStr String a ser buscadas nos parâmetro
   * @param params Parâmetros dentro de um array de objetos em que serão buscados os valores
   * @returns Retorna um array filtrado de acordo com o searchStr
   *
   * @example
   * var resultado = DevUtils.searchInArray([{ id: 1, name: 'Teste' }, { id: 2, name: 'SPTX' }], 'SPTX', ['name']);
   * // [{ id: 2, name: 'SPTX' }]
   *
   * var resultado = DevUtils.searchInArray([{ id: 1, name: 'Teste' }, { id: 2, name: 'SPTX' }], '1', ['id', 'name']);
   * // [{ id: 1, name: 'Teste' }]
   */
  public static searchInArray<T>(
    array: T[],
    searchStr: string,
    params: string[]
  ): T[] {
    return array.filter((a) => {
      return params.some((p) => {
        return a[p].toLowerCase().includes(searchStr.toLowerCase());
      });
    });
  }

  public static propertiesEqualsInArray<T>(
    array: Array<T>,
    searchStr: string,
    params: string[],
    everyProperties: boolean = false
  ) {
    if (everyProperties) {
      array.forEach((a) => {
        return params.every((p) => {
          return a[p].toLowerCase() == searchStr.toLowerCase();
        });
      });
    } else {
      array.forEach((a) => {
        return params.some((p) => {
          return a[p].toLowerCase() == searchStr.toLowerCase();
        });
      });
    }
  }

  public static get getRandomUUID(): string {
    return crypto.randomUUID();
  }

  /**
   * @description Limita um array
   * @param array Array para retornar seus childrens
   * @param count size
   * @return Novo array
   *
   * @example
   * var resultado = DevUtils.limitArray([1, 2, 3], 2);
   * // [1, 2]
   */
  public static limitArray<T = any>(array: T[], count: number): T[] {
    if (count < 0) {
      count = Number.MAX_SAFE_INTEGER;
    }

    return array.slice(0, count);
  }

  /**
   * @description Limita uma string
   * @param str: String para limitar
   * @param count size
   * @return String limitada
   *
   * @example
   * var resultado = DevUtils.limit('Teste', 2);
   * // Te
   */
  public static limit(str: string, count: number): string {
    return DevUtils.limitArray(str.split(''), count).join('');
  }

  /**
   * @description Filtra um array removendo valores duplicados
   * @param arr Array a ser filtrado
   * @returns Array filtrado por duplicados
   *
   * @example
   * var resultado = DevUtils.removeDuplicates([1, 2, 3, 1, 2, 3]);
   * // [1, 2, 3]
   */
  public static removeDuplicates<T>(arr: T[]): T[] {
    if (!Array.isArray(arr)) {
      console.error(`array expected, ${typeof arr} provided`);
      return;
    }

    return [...new Set(arr)];
  }

  /**
   * @description Filtra um array removendo valores duplicados por "id" (key)
   * @param arr Array a ser filtrado
   * @returns Array filtrado por duplicados
   *
   * @example
   * var resultado = DevUtils.removeDuplicates([1, 2, 3, 1, 2, 3]);
   * // [1, 2, 3]
   */
  public static removeDuplicatesById<T>(arr: T[], key: string): T[] {
    if (!Array.isArray(arr)) {
      console.error(`array expected, ${typeof arr} provided`);
      return arr;
    }

    return arr.filter(
      (v, i, a) => a.findIndex((v2) => v2[key] === v[key]) === i
    );
  }

  /**
   * @param obj Objeto a ser extraído as chaves
   * @param keys Chaves a serem extraídas
   * @returns Objeto com as chaves extraídas
   *
   * @example
   * var resultado = DevUtils.extract({ id: 1, name: 'Teste' }, ['id']);
   * // { id: 1 }
   *
   * var resultado = DevUtils.extract({ id: 1, name: 'Teste' }, ['id', 'name', 'exemplo']);
   * // { id: 1, name: 'Teste', exemplo: undefined }
   */
  public static extract<T>(
    obj: T,
    keys: string[],
    appendObj?: any
  ): Partial<T> {
    let extractData = keys.reduce((acc, curr) => {
      acc[curr] = obj[curr];
      return acc;
    }, {} as T);

    if (appendObj) {
      extractData = Object.assign(extractData, appendObj);
    }

    return extractData;
  }

  /**
   * @param str String a ser extraida os números
   * @returns String com apenas números
   *
   * @example
   * var resultado = DevUtils.extractNumbers('Teste 123');
   * // 123
   */
  public static extractNumbers(str: string): string {
    return str ? str.replace(/\D/g, '') : str;
  }

  /**
   * @description Converte um arquivo para base64
   * @param file Arquivo
   * @param callBack função callback (opcional)
   * @returns base64 do arquivo / void caso não seja passado um arquivo
   */
  public static async fileToBase64(
    file: File,
    callBack?: (
      result: string | ArrayBuffer | void
    ) => string | ArrayBuffer | void
  ): Promise<string | ArrayBuffer | void> {
    if (!file) return;

    try {
      function _fileToBase64(_file: File): Promise<string | ArrayBuffer> {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();

          reader.readAsDataURL(_file);

          reader.onload = () => {
            resolve(reader.result);
          };

          reader.onerror = (error) => {
            reject(error);
          };
        });
      }

      const base64 = await _fileToBase64(file);

      if (typeof callBack === 'function') {
        return callBack(base64);
      }

      return base64;
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    }
  }

  /**
   * @description Retorna por padrão a data no formato dd/MM/yyyy H:mm:ss
   * @param {Date | string} date
   * @param {string} returnType
   * @returns {string} Retorna a data formatada
   * @doc https://date-fns.org/v2.26.0/docs/format
   *
   * @example
   * const date = 2021-09-01T00:00:00.000000Z
   *
   * DevUtils.getFormatDate(date)
   * // 01/09/2021 00:00:00
   *
   * DevUtils.getFormatDate(date, "dd/MM/yyyy")
   * // 01/09/2021
   *
   * DevUtils.getFormatDate(date, "dd/MM/yyyy H:mm")
   * // 01/09/2021 00:00
   */
  public static getFormatDate(
    date: Date | string,
    returnType: string = 'dd/MM/yyyy H:mm:ss'
  ): string | null {
    try {
      // Prevenção de "Invalid date"
      if (typeof date === 'string') {
        try {
          date = new Date(date.replace('T', ' ').replace('.000000Z', ''));
        } catch (err) {
          date = new Date(date);
        }

        return format(new Date(date), returnType, { locale: ptBR });
      } else if (typeof date === 'object') {
        if (date instanceof Date) {
          return format(date, returnType, { locale: ptBR });
        } else if (typeof date === 'object') {
          return format(new Date(date), returnType, { locale: ptBR });
        }

        return format(date, returnType);
      } else {
        return null;
      }
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  /**
   *
   * @param telephone Phone number
   * @returns void
   *
   * @example
   * DevUtils.formatPhone("21-979255224") // (21) 97925-5224
   *
   */
  public static formatPhone(telephone: string) {
    telephone = telephone.replace(/\D/g, '');
    let fNumber = telephone.replace(/^(\d{2})(\d{4,5})(\d{4})$/, '($1) $2-$3');
    return fNumber;
  }

  public static getAllFormControls = (form: FormGroup<any>) => Object.keys(form.value).map((key) => ({field: key, control: form.controls[key], element: document.querySelector(`[formcontrolname="${key}"]`)})).filter(x => x.control && x.element)

  /**
   *
   * @param cookieName Cookie name
   * @param cookieValue Cookie value
   * @param minutes Cookie expiration time in minutes
   * @returns void
   *
   * @example
   * DevUtils.setCookie("myCookie", "myValue", 1)
   *
   */
  public static setCookie(
    cookieName: string,
    cookieValue: string,
    minutes: number
  ) {
    var d = new Date();
    d.setTime(d.getTime() + minutes * 60 * 1000);
    var expires = 'expires=' + d.toUTCString();
    document.cookie = cookieName + '=' + cookieValue + '; ' + expires;
  }

  /**
   * @description Get cookie value
   * @param cookieName Cookie name
   * @returns Cookie value
   *
   * @example
   * DevUtils.getCookie("myCookie")
   * // myValue
   *
   * DevUtils.getCookie("myCookie2")
   * // ""
   */
  public static getCookie(cookieName: string): string {
    let name = cookieName + '=';
    let decodedCookie = decodeURIComponent(document.cookie);
    let ca = decodedCookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) == ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) == 0) {
        return c.substring(name.length, c.length);
      }
    }

    return '';
  }

  /**
   * @param date Date
   * @returns Age
   */
  public static getAgeByDate(date: Date | string): number | null {
    try {
      const today = new Date();
      const birthDate = new Date(date);
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }

      return age;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  /**
   * @param {number} seconds Tempo em segundos
   * @returns "00:00"
   *
   * @example
   * DevUtils.secondsToMinutes(60)
   * // 01:00
   */
  public static secondsToMinutes = (seconds: number) =>
    Math.floor(seconds / 60) +
    ':' +
    String(
      Math.floor(seconds % 60) < 10
        ? '0' + Math.floor(seconds % 60)
        : Math.floor(seconds % 60)
    );

  /**
   * @param {number} seconds Tempo em segundos
   * @returns "00:00:00"
   *
   * @example
   * DevUtils.secondsToHours(3600)
   * // 01:00:00
   */
  public static secondsToHours(seconds: number): string {
    var hours = Math.floor(seconds / 3600);
    var minutes = Math.floor((seconds - hours * 3600) / 60);
    var s = seconds - hours * 3600 - minutes * 60;

    return (
      String(hours).padStart(2, '0') +
      ':' +
      String(minutes).padStart(2, '0') +
      ':' +
      String(s).padStart(2, '0')
    );
  }

  /**
   *
   * @param str string a converter
   * @param uppercase tipo de retorno
   * @returns string sem acentos
   *
   * @example
   * DevUtils.removeAccents("Olá, mundo!")
   * // Ola, mundo!
   *
   * DevUtils.removeAccents("Olá, mundo!", false)
   * // ola, mundo!
   *
   * DevUtils.removeAccents("Olá, mundo!", true)
   * // OLA, MUNDO!
   *
   */
  public static removeAccents(str: string, uppercase = null): string {
    if (typeof str !== 'string') return '';

    var withAcc =
      'ÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏÐÑÒÓÔÕÖØÙÚÛÜÝŔÞßàáâãäåæçèéêëìíîïðñòóôõöøùúûüýþÿŕ';

    var withOutAcc =
      'AAAAAAACEEEEIIIIDNOOOOOOUUUUYRsBaaaaaaaceeeeiiiionoooooouuuuybyr';

    var newStr = '';

    for (var i = 0; i < str.length; i++) {
      var switchStr = false;
      for (var a = 0; a < withAcc.length; a++) {
        if (str.substr(i, 1) == withAcc.substr(a, 1)) {
          newStr += withOutAcc.substr(a, 1);
          switchStr = true;
          break;
        }
      }
      if (switchStr == false) {
        newStr += str.substr(i, 1);
      }
    }

    if (uppercase === true) return newStr.toUpperCase();
    else if (uppercase === false) return newStr.toLowerCase();
    else return newStr;
  }
}

export class DataUtils extends Date {
  constructor()
  {
    super();
  }

  zerarHoras(){
    this.setHours(0, 0, 0, 0);
    return this;
  }
  public format(
    date: Date | string,
    returnType: string = 'dd/MM/yyyy H:mm:ss'
  ): string | null
  {
    return DevUtils.getFormatDate(date, returnType);
  }

  ddMMYYYY(separator = "/"){
    return `${this.getDate()}${separator}${this.getMonth() + 1}${separator}${this.getFullYear()}`;
  }


}

// interface PowerArray {
//   isEmpty(): boolean;
//   isNotEmpty(): boolean;
//   first(): T | undefined;
//   last(): T | undefined;
//   where(conditionFn: (value: T) => boolean): T[];
//   skip(n: number): T[];
//   orderBy(compareFn: (a: T, b: T) => number): T[];
//   groupBy(keyFn: (value: T) => string): Map<string, T[]>;
//   all(callbackFn: (value: T) => boolean): boolean;
//   take(n: number): T[];
//   get(key: string): T;
//   clean(): T[];
// }

export class PowerArray extends Array {
  constructor(...items) {
    super(...items);
  }

  isEmpty() {
    return this.length === 0;
  }

  isNotEmpty() {
    return this.length > 0;
  }

  first(): any | undefined {
    return this[0];
  }

  last(): any | undefined {
    return this[this.length - 1];
  }

  where(conditionFn: (value: any) => boolean): any {
    const result = [];
    for (const item of this) {
      if (conditionFn(item)) {
        result.push(item);
      }
    }
    return result;
  }

  skip(n: number) {
    return this.slice(n);
  }

  orderBy(compareFn: (a: any, b: any) => number) {
    return [...this].sort(compareFn);
  }

  groupBy(keyFn) {
    const groups = new Map();
    for (const item of this) {
      const key = keyFn(item);
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key).push(item);
    }
    return groups;
  }

  all(callbackFn: (value: any) => boolean): boolean {
    for (const item of this) {
      if (!callbackFn(item)) {
        return false;
      }
    }
    return true;
  }

  take(n: number) {
    return this.slice(0, n);
  }

  get(key: string) {
    return this[key];
  }

  clean() {
    return this.filter((v, i, a) => a);
  }
}
